import {
  AutomationActionType,
  AutomationContext,
} from '@kinet-open-network/shared/data-access/model';

// ─── Action data interfaces ────────────────────────────────────────────────

export interface IAutomationActionRefreshData {
  refreshDailyChanges?: boolean;
}

export interface IAutomationActionRefreshStockMovesData {
  fromDate?: Date;
}

export interface IAutomationActionWriteOrderToOnixData {
  disableAddresses?: boolean;
}

export interface IAutomationActionGenerateCustomerRentalInvoiceData {
  date?: Date;
}

export interface IAutomationActionCompanyRegistryRefreshAllData {
  mode?: 'full' | 'incremental';
}

export interface IAutomationActionWriteBottleOwnersToOnixData {
  date?: Date;
}

export interface IAutomationActionRegenerateOrderStockMovesData {
  /** Used in the global context only - in the order context the action runs for that order. */
  fromDate?: Date;
  toDate?: Date;
}

/** Creates a draft order from the output of the preceding extraction (`ai_prompt`) action. */

// ─── Type augmentations ────────────────────────────────────────────────────

declare module '@kinet-open-network/shared/data-access/model' {
  /** Extends the automation action type union with pbgas ONIX actions. */
  interface AutomationActionTypeExtensions {
    pbgas:
      | 'write-customer-to-onix'
      | 'refresh-stock-items'
      | 'refresh-customers'
      | 'refresh-group-prices'
      | 'refresh-customer-prices'
      | 'refresh-stock-moves'
      | 'refresh-stock-item-tags'
      | 'refresh-orders'
      | 'write-order-to-onix'
      | 'check-order-onix-release'
      | 'write-delivery-to-onix'
      | 'generate-customer-rental-invoice'
      | 'company-registry-refresh-all'
      | 'company-registry-sync-dic-icdph'
      | 'write-bottle-owners-to-onix'
      | 'create-order-draft'
      | 'regenerate-order-stock-moves';
  }

  /** Maps pbgas ONIX action types to their contexts. */
  interface AutomationActionToContextMap {
    'write-customer-to-onix': 'customer';
    'refresh-stock-items': 'global';
    'refresh-customers': 'global';
    'refresh-group-prices': 'global';
    'refresh-customer-prices': 'global';
    'refresh-stock-moves': 'global';
    'refresh-stock-item-tags': 'global';
    'refresh-orders': 'global';
    'write-order-to-onix': 'order';
    'check-order-onix-release': 'order';
    'write-delivery-to-onix': 'delivery';
    'generate-customer-rental-invoice': 'customer';
    'company-registry-refresh-all': 'global';
    'company-registry-sync-dic-icdph': 'global';
    'write-bottle-owners-to-onix': 'global';
    'create-order-draft': 'message';
    'regenerate-order-stock-moves': 'order' | 'global';
  }

  /** Data shapes for pbgas ONIX action types. */
  interface IAutomationActionDataMap {
    'refresh-stock-items': IAutomationActionRefreshData;
    'refresh-customers': IAutomationActionRefreshData;
    'refresh-group-prices': unknown;
    'refresh-customer-prices': unknown;
    'refresh-stock-moves': IAutomationActionRefreshStockMovesData;
    'refresh-stock-item-tags': unknown;
    'refresh-orders': IAutomationActionRefreshData;
    'write-order-to-onix': IAutomationActionWriteOrderToOnixData;
    'check-order-onix-release': unknown;
    'write-delivery-to-onix': unknown;
    'write-customer-to-onix': unknown;
    'generate-customer-rental-invoice': IAutomationActionGenerateCustomerRentalInvoiceData;
    'company-registry-refresh-all': IAutomationActionCompanyRegistryRefreshAllData;
    'company-registry-sync-dic-icdph': unknown;
    'write-bottle-owners-to-onix': IAutomationActionWriteBottleOwnersToOnixData;
    'create-order-draft': unknown;
    'regenerate-order-stock-moves': IAutomationActionRegenerateOrderStockMovesData;
  }
}

// ─── Action type identifiers ──────────────────────────────────────────────────

export const pbgasAutomationActionTypes = [
  'write-customer-to-onix',
  'refresh-stock-items',
  'refresh-customers',
  'refresh-group-prices',
  'refresh-customer-prices',
  'refresh-stock-moves',
  'refresh-stock-item-tags',
  'refresh-orders',
  'write-order-to-onix',
  'check-order-onix-release',
  'write-delivery-to-onix',
  'generate-customer-rental-invoice',
  'company-registry-refresh-all',
  'company-registry-sync-dic-icdph',
  'write-bottle-owners-to-onix',
  'create-order-draft',
  'regenerate-order-stock-moves',
] as const;
export type PbgasAutomationActionType =
  (typeof pbgasAutomationActionTypes)[number];

// ─── Registration data (used by app.config) ────────────────────────────

/**
 * Custom automation actions registration data.
 */
export const pbgasAutomationActionsData: {
  actionType: AutomationActionType;
  label: string;
  icon?: string;
  context: AutomationContext;
  sidebarAction?: boolean;
}[] = [
  {
    actionType: 'write-customer-to-onix',
    label: '[ONIX] Zapísať zákazníka',
    context: 'customer',
  },
  {
    actionType: 'refresh-stock-items',
    label: '[ONIX] Obnoviť skladové položky',
    context: 'global',
  },
  {
    actionType: 'refresh-customers',
    label: '[ONIX] Obnoviť zákazníkov',
    context: 'global',
  },
  {
    actionType: 'refresh-group-prices',
    label: '[ONIX] Obnoviť skupinové ceny',
    context: 'global',
  },
  {
    actionType: 'refresh-customer-prices',
    label: '[ONIX] Obnoviť zákaznícke ceny',
    context: 'global',
  },
  {
    actionType: 'refresh-stock-moves',
    label: '[ONIX] Obnoviť skladové pohyby',
    context: 'global',
  },
  {
    actionType: 'refresh-stock-item-tags',
    label: '[ONIX] Obnoviť tagy skladových položiek',
    context: 'global',
  },
  {
    actionType: 'refresh-orders',
    label: '[ONIX] Obnoviť objednávky',
    context: 'global',
  },
  {
    actionType: 'write-order-to-onix',
    label: '[ONIX] Zapísať objednávku',
    icon: 'pi pi-send',
    context: 'order',
  },
  {
    actionType: 'check-order-onix-release',
    label: '[ONIX] Skontrolovať výdajku objednávky',
    icon: 'pi pi-check-square',
    context: 'order',
  },
  {
    actionType: 'write-delivery-to-onix',
    label: '[ONIX] Zapísať dodávku',
    icon: 'pi pi-send',
    context: 'delivery',
  },
  {
    actionType: 'generate-customer-rental-invoice',
    label: 'Generovať faktúru za prenájom',
    icon: 'pi pi-refresh',
    context: 'customer',
    sidebarAction: false,
  },
  {
    actionType: 'company-registry-refresh-all',
    label: '[Register firiem] Obnoviť register firiem',
    context: 'global',
  },
  {
    actionType: 'company-registry-sync-dic-icdph',
    label: '[Register firiem] Obnoviť DIČ/IČ DPH',
    context: 'global',
  },
  {
    actionType: 'write-bottle-owners-to-onix',
    label: '[ONIX] Zapísať vlastníkov fliaš',
    context: 'global',
  },
  {
    actionType: 'create-order-draft',
    label: 'Vytvoriť draft objednávky zo správy',
    icon: 'pi pi-shopping-cart',
    context: 'message',
  },
  {
    actionType: 'regenerate-order-stock-moves',
    label: 'Pregenerovať skladové pohyby objednávok',
    icon: 'pi pi-sync',
    context: 'order',
  },
  // Same action in the global context: runs for all orders within the date range.
  {
    actionType: 'regenerate-order-stock-moves',
    label: 'Pregenerovať skladové pohyby objednávok',
    icon: 'pi pi-sync',
    context: 'global',
  },
];
